import { useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Box,
  TextField,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

/**
 * Dialog for AI-powered labor time estimation.
 * User describes the work in free text, AI estimates hours and returns work steps.
 */
function AiEstimateJobDialog({ open, handleClose, maintenance, setMaintenance, onJobCreated }) {
  const { t, i18n } = useTranslation();
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setDescription('');
      setError(null);
      setLoading(false);
    }
  }, [open]);

  const vehicle = maintenance?.vehicle;

  const handleSubmit = async () => {
    if (!description.trim() || loading) return;

    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(
        `/api/maintenances/${maintenance.id}/ai_estimate_job/`,
        {
          description: description.trim(),
          language: i18n.language || 'fi',
        }
      );

      const data = response.data;
      const createdJobId = data.created_job_id;
      setMaintenance(data);
      onJobCreated(data, createdJobId);
    } catch (err) {
      const msg = err.response?.data?.error || t('AI estimation failed');
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      handleSubmit();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: '12px' } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoFixHighIcon sx={{ color: 'info.main' }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('AI labor estimate')}
          </Typography>
        </Box>
        {!loading && (
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: '13px', color: 'text.secondary', mb: 2 }}>
          {t('AI estimate info')}
        </Typography>

        {/* Vehicle summary */}
        {vehicle && (
          <Box
            sx={{
              p: 1.5,
              mb: 2,
              bgcolor: 'action.hover',
              borderRadius: '8px',
              display: 'flex',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>
              {vehicle.manufacturer} {vehicle.model}
            </Typography>
            {vehicle.year_model && (
              <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                {vehicle.year_model}
              </Typography>
            )}
            {vehicle.engine_type && (
              <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                &middot; {vehicle.engine_type}
              </Typography>
            )}
            {vehicle.power_kw && (
              <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                &middot; {vehicle.power_kw} kW
              </Typography>
            )}
          </Box>
        )}

        {/* Job description input */}
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={3}
          maxRows={6}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t('Describe the work to estimate...')}
          disabled={loading}
          sx={{ mb: 1 }}
        />

        {/* Loading state */}
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }}>
            <CircularProgress size={20} />
            <Typography sx={{ fontSize: '14px', color: 'text.secondary' }}>
              {t('Estimating...')}
            </Typography>
          </Box>
        )}

        {/* Error state */}
        {error && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          {t('Cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!description.trim() || loading}
          startIcon={loading ? <CircularProgress size={16} /> : <AutoFixHighIcon />}
        >
          {t('Estimate')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AiEstimateJobDialog;
